import { Component, computed, inject, type OnDestroy, type OnInit, signal } from "@angular/core";
import { Router, RouterLink } from "@angular/router";
import { TranslatePipe } from "@ngx-translate/core";
import { Subscription } from "rxjs";

import { PermissionActionDescriptorType } from "../../../../adaa-types";
import { AdaaHelper, isDsV2, isDsV3 } from "../../../core/utils";
import {
  AggregatedPerformanceComponent,
  AiPerformanceComponent,
  GovEnablerPerformanceComponent,
  GovVisionPerformanceComponent,
  NationalStrategiesPerformanceComponent,
  PeriodSliderComponent,
  UaeVisionPerformanceComponent,
} from "../../../shared/components";
import { Constants } from "../../../shared/constants/constants";
import { PermissionAction } from "../../../shared/constants/enums";
import { HasPermissionDirective } from "../../../shared/directives";
import type { SelectedPeriodModelType } from "../../../shared/models";
import {
  AggregationPerfService,
  AppService,
  GovEnablerPerfService,
  NationalStrategiesPerfService,
  SystemLayoutService,
  UaeVisionPerfService,
} from "../../../shared/services";

@Component({
  selector: "adaa-pmo",
  templateUrl: "./pmo.html",
  styleUrl: "./pmo.scss",
  providers: [AggregationPerfService],
  imports: [
    RouterLink,
    TranslatePipe,
    PeriodSliderComponent,
    AggregatedPerformanceComponent,
    UaeVisionPerformanceComponent,
    GovEnablerPerformanceComponent,
    NationalStrategiesPerformanceComponent,
    GovVisionPerformanceComponent,
    HasPermissionDirective,
    AiPerformanceComponent,
  ],
})
export class PmoComponent implements OnInit, OnDestroy {
  readonly systemLayoutService = inject(SystemLayoutService);
  private readonly _router = inject(Router);
  private readonly _appService = inject(AppService);
  private readonly _nationalStrategiesPerfService = inject(NationalStrategiesPerfService);
  private readonly _govEnablerPerfService = inject(GovEnablerPerfService);
  private readonly _uaeVisionPerfService = inject(UaeVisionPerfService);
  private readonly _aggregationPerfService = inject(AggregationPerfService);

  selectedPeriod = signal<SelectedPeriodModelType | undefined>(undefined);

  aggregationPerf = computed(() => this._aggregationPerfService.aggregationPerf());
  hasKPIsWithIssue = computed(() => this._aggregationPerfService.hasKPIsWithIssue());

  readonly frequency = Constants.FREQUENCY.QUARTERLY;
  readonly isDsV2orV3 = () => isDsV2() || isDsV3();

  private readonly _subscriptions = new Subscription();

  public get numOfAggTypes() {
    return this.aggregationPerf().length;
  }

  readonly overallPerformancePermission: PermissionActionDescriptorType = {
    actor: {
      modifier: "or",
      permissions: [
        {
          permissionAction: PermissionAction.view,
          objectTypeId: Constants.CONSTANT_PERMISSIONS.PMO_HOMEPAGE_OVERALL_PERFORMANCE,
        },
      ],
    },
  };

  readonly weTheUaePermission: PermissionActionDescriptorType = {
    actor: {
      modifier: "or",
      permissions: [
        {
          permissionAction: PermissionAction.view,
          objectTypeId: Constants.CONSTANT_PERMISSIONS.PMO_HOMEPAGE_WTUAE_PERFORMANCE,
        },
        {
          permissionAction: PermissionAction.view,
          objectTypeId: Constants.CONSTANT_PERMISSIONS.PMO_HOMEPAGE_WTUAE_PILLARS,
        },
      ],
    },
  };

  readonly enablerPermission: PermissionActionDescriptorType = {
    actor: {
      modifier: "or",
      permissions: [
        {
          permissionAction: PermissionAction.view,
          objectTypeId: Constants.CONSTANT_PERMISSIONS.PMO_HOMEPAGE_ENABLER_OVERALL,
        },
        {
          permissionAction: PermissionAction.view,
          objectTypeId: Constants.CONSTANT_PERMISSIONS.PMO_HOMEPAGE_ENABLER_PILLARS,
        },
      ],
    },
  };

  readonly aiKpiPermission: PermissionActionDescriptorType = {
    actor: {
      modifier: "or",
      permissions: [
        {
          permissionAction: PermissionAction.view,
          objectTypeId: Constants.CONSTANT_PERMISSIONS.AIKPI,
        },
        {
          permissionAction: PermissionAction.view,
          objectTypeId: Constants.CONSTANT_PERMISSIONS.AGENTIC_DASHBOARD,
        },
      ],
    },
  };

  readonly nationalStrategiesPermission: PermissionActionDescriptorType = {
    actor: {
      modifier: "or",
      permissions: [
        {
          permissionAction: PermissionAction.view,
          objectTypeId: Constants.CONSTANT_PERMISSIONS.PMO_HOMEPAGE_NATIONAL_STRATEGIES,
        },
      ],
    },
  };

  public ngOnInit() {
    this._updateActiveEntity();
    this._refresh();
  }

  public ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }

  public selectPeriod(data: SelectedPeriodModelType) {
    if (!data || !this._appService.hasPlanId()) {
      return;
    }

    const { period, type } = data;
    if (type === "quarter") {
      this._aggregationPerfService.pmoQuarterlyPerformance(period.id).subscribe();
      this._uaeVisionPerfService.getQuarterlyVisionDirections(period.id);
      this._govEnablerPerfService.getQuarterlyGovEnablerPillars(period.id);
      this._nationalStrategiesPerfService.getQuarterlyGovEnablerDirections(period.id);
    } else if (type === "year") {
      this._aggregationPerfService.pmoAnnualPerformance(period.year).subscribe();
      this._uaeVisionPerfService.getAnnualVisionDirections(period.year);
      this._govEnablerPerfService.getAnnualGovEnablerPillars(period.year);
      this._nationalStrategiesPerfService.getAnnualGovEnablerDirections(period.year);
    }
    this._aggregationPerfService.getCountAuditIssues(period.id, Constants.CONSTANT_PMO_ID).subscribe();
    this.selectedPeriod.set({ period, type });
  }

  private _updateActiveEntity() {
    const entityId = AdaaHelper.getLocalStorage(Constants.localStorageKeys.currentEntity, {
      type: "prop",
      property: "id",
    }) as number | undefined;

    if (!entityId) return;

    if (entityId !== Constants.CONSTANT_PMO_ID) {
      this._router.navigateByUrl("/dashboard/entity");
    }
  }

  private _refresh() {
    this._subscriptions.add(
      this.systemLayoutService.hasActiveEntityChanged$.subscribe(() => {
        this._updateActiveEntity();
      })
    );

    this._subscriptions.add(
      this.systemLayoutService.hasCycleChanged$.subscribe({
        next: () => window.location.reload(),
      })
    );
  }
}
